import { useLoaderData } from "react-router";
import { Table } from "@/components/application/table/table";
import { CopyField } from "@/components/admin/copy-field";
import { Deferred, FieldSkeleton, Loading, Skeleton, TableSkeleton } from "@/components/admin/loading";
import { Badge } from "@/components/base/badges/badges";
import { formatDate, type WorkspaceDetail } from "../lib/api.js";
import { Card, PageHeader } from "./shell.jsx";

const ROLES: Record<string, string> = { owner: "所有者", admin: "管理员", member: "成员" };

/** Its name and age, its id and slug, then its members. */
export function WorkspaceSkeleton() {
  return (
    <Loading>
      <PageHeader title={<Skeleton className="my-1 h-6 w-40" />} description={<Skeleton className="my-0.5 w-44" />} />
      <Card>
        <div className="grid gap-5 p-6 sm:grid-cols-2">
          <FieldSkeleton label="工作区 ID" />
          <FieldSkeleton label="标识" />
        </div>
      </Card>
      <Card title={<Skeleton className="my-0.5 h-5 w-20" />}>
        <TableSkeleton columns={["姓名", "电子邮件", "角色", "加入时间"]} rows={3} size="sm" bordered={false} />
      </Card>
    </Loading>
  );
}

/** One workspace and who is in it. What it holds is its members' to read, not an administrator's. */
export function WorkspaceRoute() {
  const { data } = useLoaderData<{ data: Promise<WorkspaceDetail> }>();
  return (
    <Deferred data={data} fallback={<WorkspaceSkeleton />}>
      {(workspace) => <Workspace workspace={workspace} />}
    </Deferred>
  );
}

function Workspace({ workspace }: { workspace: WorkspaceDetail }) {
  return (
    <>
      <PageHeader title={workspace.name} description={`创建于 ${formatDate(workspace.createdAt)}`} />
      <Card>
        <div className="grid gap-5 p-6 sm:grid-cols-2">
          <CopyField label="工作区 ID" value={workspace.id} />
          <CopyField label="标识" value={workspace.slug} />
        </div>
      </Card>
      <Card title={`成员（${workspace.members.length}）`}>
        {workspace.members.length === 0 ? (
          <p className="px-6 py-8 text-sm text-tertiary">这个工作区没有成员。</p>
        ) : (
          <Table aria-label="成员" size="sm">
            <Table.Header bordered={false}>
              <Table.Head id="name" label="姓名" isRowHeader />
              <Table.Head id="email" label="电子邮件" />
              <Table.Head id="role" label="角色" />
              <Table.Head id="joinedAt" label="加入时间" />
            </Table.Header>
            <Table.Body items={workspace.members}>
              {(m) => (
                <Table.Row id={m.id} href={`/users/${m.userId}`}>
                  <Table.Cell className="font-medium text-primary">{m.name || "未知"}</Table.Cell>
                  <Table.Cell>{m.email}</Table.Cell>
                  <Table.Cell>
                    <Badge color={m.role.includes("owner") ? "brand" : "gray"} size="sm">
                      {m.role.split(",").map((r) => ROLES[r.trim()] ?? r.trim()).join("、")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{formatDate(m.joinedAt)}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        )}
      </Card>
    </>
  );
}
